import {
  Button,
  Checkbox,
  Clickable,
  Icon,
  IconButton,
  P,
  Tabs,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import React, { useEffect, useState, useContext } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import cn from 'classnames';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { useCounterContext } from '../../hooks/useCounter';
import {
  CounterQuoteFormValues,
  CounterQuoteStatus,
  counterQuoteStatusDisplayMapping,
  CounterQuoteStatusToHide,
  QuantityItem,
} from '../../types';
import { FeatureFlagsContext } from '../../../../Lib/Contexts/FeatureFlagsContext';
import { BASELINE_COST_TOOLTIP } from '../../../../Lib/Constants/ui-text';

function QuantityForm() {
  const { selectedCounter } = useCounterContext();
  const { watch, setValue, control, getValues } =
    useFormContext<CounterQuoteFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'quantity' as const,
  }) as unknown as {
    fields: (QuantityItem & { id: string })[];
    append: (value: QuantityItem) => void;
    remove: (index: number) => void;
  };
  const { flags } = useContext(FeatureFlagsContext);

  const isPlusCostNA = watch('isPlusCostNotApplicable') ?? false;

  const quoteStatusToHide = Object.values(CounterQuoteStatusToHide) as string[];

  const quoteStatus = quoteStatusToHide.includes(
    selectedCounter?.counter_quote_status as string,
  )
    ? selectedCounter?.prev_counter_quote_status
    : (selectedCounter?.counter_quote_status as CounterQuoteStatus);

  const mappedStatus =
    counterQuoteStatusDisplayMapping[
      quoteStatus as keyof typeof counterQuoteStatusDisplayMapping
    ];
  const formEnabled = [
    CounterQuoteStatus.NOT_SENT,
    CounterQuoteStatus.SENT,
  ].includes(mappedStatus as CounterQuoteStatus);

  const type = watch('type');
  const [previousValues, setPreviousValues] = useState<{
    range?: Array<QuantityItem>;
    any?: QuantityItem;
  }>({});

  const quantities = watch('quantity');

  const lastToValue = Array.isArray(quantities)
    ? (quantities[quantities.length - 1]?.max_range ?? 0)
    : 0;

  const fieldConfig = [
    {
      name: 'min_range',
      label: 'From',
      inputType: 'number' as const,
      inputProps: {
        endAdornment: (
          <P size="xs" color="disabled">
            qty
          </P>
        ),
      },
    },
    {
      name: 'max_range',
      label: 'To',
      inputType: 'number' as const,
      inputProps: {
        endAdornment: (
          <P size="xs" color="disabled">
            qty
          </P>
        ),
      },
    },
    {
      name: 'jr_cost',
      label: 'JR Cost',
      inputType: 'currency' as const,
      inputProps: {
        currency: '$',
      },
    },
    {
      name: 'plus_cost',
      label: 'Plus Cost',
      inputType: 'currency' as const,
      inputProps: {
        currency: '$',
      },
    },
    ...(flags?.baseline_cost?.enabled
      ? [
          {
            name: 'baseline_jr_cost',
            label: 'Baseline JR Cost',
            inputType: 'currency' as const,
            inputProps: {
              currency: '$',
              className: 'w-full',
              style: { width: '100%' },
            },
            tooltip: BASELINE_COST_TOOLTIP,
          },
          {
            name: 'baseline_plus_cost',
            label: 'Baseline Plus Cost',
            inputType: 'currency' as const,
            inputProps: {
              currency: '$',
              className: 'w-full',
              style: { width: '100%' },
            },
            tooltip: BASELINE_COST_TOOLTIP,
          },
        ]
      : []),
  ];

  const validationRules = (
    fieldName: string,
    fieldLabel: string,
    index?: number,
  ) => ({
    valueAsNumber: true,
    validate: (val: unknown) => {
      const value = val as number;
      if (value < 0) return `${fieldLabel} must be greater than or equal to 0`;

      const plusCostNA = getValues('isPlusCostNotApplicable');
      if (plusCostNA || value !== 0) return true;

      const quantity = getValues('quantity');
      const currentQuantity = Array.isArray(quantity)
        ? quantity[index ?? 0]
        : quantity;

      if (fieldName === 'jr_cost' && currentQuantity.plus_cost === 0) {
        return 'Both Jr. Cost and Plus Cost cannot be 0';
      }
      if (fieldName === 'plus_cost' && currentQuantity.jr_cost === 0) {
        return 'Both Jr. Cost and Plus Cost cannot be 0';
      }
      return true;
    },
  });

  useEffect(() => {
    if (!selectedCounter?.counter_quote) {
      return;
    }
    const currentQuantity = getValues('quantity');
    if (type === 'range' && !Array.isArray(currentQuantity)) {
      setPreviousValues((prev) => ({ ...prev, any: currentQuantity }));
      setValue(
        'quantity',
        previousValues.range || [
          {
            min_range: '',
            max_range: '',
            jr_cost: '',
            plus_cost: '',
          },
        ],
      );
    } else if (type === 'any' && Array.isArray(currentQuantity)) {
      setPreviousValues((prev) => ({ ...prev, range: currentQuantity }));
      setValue(
        'quantity',
        previousValues.any || {
          min_range: '',
          max_range: '',
          jr_cost: '',
          plus_cost: '',
        },
      );
    }
  }, [type, setValue, getValues, selectedCounter?.division]);

  useEffect(() => {
    setPreviousValues({});
  }, [selectedCounter]);

  // Helper function to render input field (with optional tooltip)
  const renderInputField = (
    field: any,
    anyQuantity = false,
    index?: number,
  ) => {
    const isPlusCostField =
      field.name === 'plus_cost' || field.name === 'baseline_plus_cost';
    const isRequired =
      !field.name.startsWith('baseline_') && !(isPlusCostField && isPlusCostNA);

    const inputComponent = (
      <div className="w-full">
        <ControlledFormInput
          control={control}
          name={
            anyQuantity
              ? `quantity.${field.name}`
              : `quantity.${index}.${field.name}`
          }
          label={
            field.tooltip ? (
              <span className="flex items-center">
                {field.label}
                <Tooltip
                  content={field.tooltip}
                  position="top"
                  arrow
                  zIndex={60}
                >
                  <Icon.InformationCircle className="ml-1 text-neutral-400 text-sm flex-shrink-0" />
                </Tooltip>
              </span>
            ) : (
              field.label
            )
          }
          inputType={field.inputType}
          required={isRequired}
          rules={validationRules(field.name, field.label, index)}
          disabled={!formEnabled || field.name.startsWith('baseline_')}
          inputProps={{
            ...field.inputProps,
            className: cn(field.inputProps?.className, 'w-full'),
            style: { ...field.inputProps?.style, width: '100%' },
          }}
        />
      </div>
    );

    return inputComponent;
  };

  return (
    <div className="border rounded-xl border-neutral-200 p-5">
      <Tabs.Group type="segment">
        <Clickable onClick={() => setValue('type', 'range')}>
          <Tabs.Item
            isActive={type === 'range'}
            icon="truck/truck-trash"
            type="segment"
          >
            Quantity Range
          </Tabs.Item>
        </Clickable>
        <Clickable onClick={() => setValue('type', 'any')}>
          <Tabs.Item icon="circle-dot" type="segment" isActive={type === 'any'}>
            Any Quantity
          </Tabs.Item>
        </Clickable>
      </Tabs.Group>

      {type === 'any' ? (
        <div className="flex flex-col gap-4 mt-4">
          <div className="flex flex-wrap gap-4">
            {fieldConfig.slice(2).map((field) => (
              <div
                key={field.name}
                className={cn('flex-1 min-w-[200px] w-full', {
                  hidden:
                    isPlusCostNA &&
                    (field.name === 'plus_cost' ||
                      field.name === 'baseline_plus_cost'),
                })}
              >
                {renderInputField(field, true)}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={isPlusCostNA}
              onChangeValue={(v) => setValue('isPlusCostNotApplicable', v)}
              disabled={!formEnabled}
              tabIndex={-1}
            />
            <span className="text-sm text-gray-600">
              Plus Cost not applicable to this product.
            </span>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          {fields.map((f, index) => {
            const gridClassName = cn(
              'grid gap-2 mb-4',
              {
                'grid-cols-2': flags?.baseline_cost?.enabled,
                'grid-cols-[repeat(4,minmax(0,1fr))_32px] items-end':
                  !flags?.baseline_cost?.enabled && fields.length > 1,
                'grid-cols-4 items-end':
                  !flags?.baseline_cost?.enabled && fields.length <= 1,
              },
              fields.length > 1 &&
                !flags?.baseline_cost?.enabled &&
                'items-end',
            );

            return (
              <div
                className={cn(gridClassName, 'bg-neutral-100 p-5')}
                key={f.id}
              >
                {flags?.baseline_cost?.enabled ? (
                  <>
                    {/* New Design with Baseline Cost */}
                    <div>{renderInputField(fieldConfig[0], false, index)}</div>
                    <div>{renderInputField(fieldConfig[1], false, index)}</div>

                    {/* Second row: JR Cost and Plus Cost */}
                    <div>{renderInputField(fieldConfig[2], false, index)}</div>
                    <div className={isPlusCostNA ? 'hidden' : ''}>
                      {renderInputField(fieldConfig[3], false, index)}
                    </div>

                    {/* Third row: Baseline JR Cost and Baseline Plus Cost */}
                    <div className="w-full">
                      {renderInputField(fieldConfig[4], false, index)}
                    </div>
                    <div className="w-full flex items-center">
                      <div
                        className={cn('flex-grow', {
                          hidden: isPlusCostNA,
                        })}
                      >
                        {renderInputField(fieldConfig[5], false, index)}
                      </div>
                      {fields.length > 1 && index === fields.length - 1 && (
                        <IconButton
                          label="remove"
                          color="secondary"
                          icon={<Icon name="delete/delete-dustbin-01" />}
                          onClick={() => remove(index)}
                          className="ml-2 mt-6 flex-shrink-0"
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Old Design without Baseline Cost */}
                    {fieldConfig.slice(0, 4).map((field) => (
                      <div
                        key={field.name}
                        className={cn({
                          hidden: isPlusCostNA && field.name === 'plus_cost',
                        })}
                      >
                        {renderInputField(field, false, index)}
                      </div>
                    ))}
                    {fields.length > 1 && index === fields.length - 1 && (
                      <div className="flex items-center justify-center">
                        <IconButton
                          label="remove"
                          color="secondary"
                          icon={<Icon name="delete/delete-dustbin-01" />}
                          onClick={() => remove(index)}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          <div className="flex items-center gap-2 mb-2 cursor-pointer select-none">
            <Checkbox
              checked={isPlusCostNA}
              onChangeValue={(v) => setValue('isPlusCostNotApplicable', v)}
              disabled={!formEnabled}
              tabIndex={-1}
            />
            <span className="text-sm text-gray-600">
              Plus Cost not applicable to this product.
            </span>
          </div>
          <Button
            onClick={() => {
              const newFrom = lastToValue || '';
              const lastRange = Array.isArray(quantities)
                ? quantities[quantities.length - 1]
                : undefined;
              append({
                min_range: newFrom,
                max_range: newFrom,
                jr_cost: '',
                plus_cost: '',
                ...(flags?.baseline_cost?.enabled
                  ? {
                      baseline_jr_cost:
                        lastRange?.baseline_jr_cost ??
                        selectedCounter?.baseline_jr_cost ??
                        '',
                      baseline_plus_cost:
                        lastRange?.baseline_plus_cost ??
                        selectedCounter?.baseline_plus_cost ??
                        '',
                    }
                  : {}),
              });
            }}
          >
            Add New
          </Button>
        </div>
      )}
    </div>
  );
}

export default QuantityForm;
